import { useEffect } from 'react';

/**
 * Inject `Cache-Control: no-store` + `Pragma: no-cache` meta tags into <head>
 * while the user is authenticated.
 *
 * Why: protects against a logged-out user clicking "Back" in a shared browser
 * and seeing the previous user's authenticated screens rendered from the bfcache.
 * Combined with destroySession()'s queryClient.clear() this means the back-nav
 * shell has no fresh data to render even if it does load.
 */
export function useNoStoreMeta(active: boolean) {
  useEffect(() => {
    if (!active) return;

    const tags: HTMLMetaElement[] = [];
    const make = (httpEquiv: string, content: string) => {
      const el = document.createElement('meta');
      el.setAttribute('http-equiv', httpEquiv);
      el.setAttribute('content', content);
      document.head.appendChild(el);
      tags.push(el);
    };

    make('Cache-Control', 'no-store, no-cache, must-revalidate, max-age=0');
    make('Pragma', 'no-cache');
    make('Expires', '0');

    // Telemetry: detect when the page is restored from bfcache while we are
    // (or were) authenticated — this is the back-button-after-logout vector.
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      import('@/utils/telemetry')
        .then(({ logEvent }) =>
          logEvent('auth.bfcache_restore_while_auth', {
            severity: 'warn',
            detail: { active },
          }),
        )
        .catch(() => {});
    };
    window.addEventListener('pageshow', onPageShow);

    return () => {
      tags.forEach((t) => {
        try { document.head.removeChild(t); } catch {}
      });
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [active]);
}
